// Which kind of problem a refusal was, in the person's words (ai_app_backend #1678, its PR #1689). When v2's safety
// check refuses words it names one fixed public code, with `public_only` (true when a picture may not be published but
// may stay private, false otherwise):
// - `422 words_refused` names it as `reason`;
// - `409 keep_refused` and `publish_refused` already had `reason: "words"` (refused for its words, beside
//   `early_access`, `age_check`, `feed_rule` …), so they name it as `words_reason`; their `reason` is never a code.
// An adapter carries the code onto the v1-shaped answer it makes as `reason` (`refusalFields`), which the screens word
// through `t()` (`refusalLine`); nothing else of the refusal is shown. The server decides. No code (an older v2, a
// listed word, a folder or persona check, a chat worker's refusal, a picture refused before) leaves each screen as
// before.

/** The codes v2 may send as a refusal's `reason`; any other value is worded as `other`. */
export const REFUSAL_REASONS = [
  'minors',
  'real_people',
  'extreme_violence',
  'named_characters',
  'illegal_drugs',
  'sexual_violence',
  'animals',
  'incest',
  'bodily_waste',
  'hate',
  'other',
] as const

export type RefusalReason = (typeof REFUSAL_REASONS)[number]

/** The translation key of each reason's line (`en` holds the English). */
export const REASON_KEYS: Record<RefusalReason, string> = {
  minors: 'refusalMinors',
  real_people: 'refusalRealPeople',
  extreme_violence: 'refusalExtremeViolence',
  named_characters: 'refusalNamedCharacters',
  illegal_drugs: 'refusalIllegalDrugs',
  sexual_violence: 'refusalSexualViolence',
  animals: 'refusalAnimals',
  incest: 'refusalIncest',
  bodily_waste: 'refusalBodilyWaste',
  hate: 'refusalHate',
  other: 'refusalOther',
}

/** What follows the reason: "Change your prompt" where a prompt was refused, "Change it" for a chat or character. */
export type RefusalFix = 'prompt' | 'it'

const FIX_KEYS: Record<RefusalFix, string> = {
  prompt: 'refusalChangePrompt',
  it: 'refusalChangeIt',
}

/** The line said first when a picture may not be published but may be kept private. */
export const PUBLIC_ONLY_KEY = 'refusalPublicOnly'

/** The words of the button that keeps such a picture private instead. */
export const KEEP_PRIVATE_KEY = 'refusalKeepPrivate'

type T = (key: string) => string

const KNOWN = new Set<string>(REFUSAL_REASONS)

type Fields = {
  code?: unknown
  reason?: unknown
  words_reason?: unknown
  public_only?: unknown
}

const fieldsOf = (body: unknown): Fields =>
  typeof body === 'object' && body !== null ? (body as Fields) : {}

/**
 * The judge's code as sent, or null: `words_reason` when v2 sends it (a refusal to keep or publish); otherwise `reason`
 * on a `words_refused`, or on an adapter's v1-shaped answer (no `code`) that carries it. Any other refusal's `reason`
 * is its own word (`keep_refused`'s `words` or `early_access`, `reference_refused`'s `no_face` …), never a code, and
 * `words` never is.
 */
function codeOf(body: unknown): string | null {
  const { code, reason, words_reason } = fieldsOf(body)
  if (typeof words_reason === 'string' && words_reason) return words_reason
  if (code !== undefined && code !== 'words_refused') return null
  return typeof reason === 'string' && reason && reason !== 'words'
    ? reason
    : null
}

/** The reason a refusal names, as one of the codes worded here (an unknown one is `other`), or null when it names none. */
export function refusalReason(body: unknown): RefusalReason | null {
  const reason = codeOf(body)
  if (!reason) return null
  return KNOWN.has(reason) ? (reason as RefusalReason) : 'other'
}

/** Whether the refusal is for publishing only: the picture may stay private. */
export const isPublicOnly = (body: unknown) =>
  refusalReason(body) !== null && fieldsOf(body).public_only === true

/**
 * The fields of v2's refusal a screen words, to carry onto the v1-shaped answer an adapter makes: the judge's code as
 * `reason` (`words_refused`'s `reason`, or keep's and publish's `words_reason`), and `public_only` when true; nothing
 * when v2 named no code, so an older v2's answer is unchanged.
 */
export function refusalFields(body: unknown): {
  reason?: string
  public_only?: true
} {
  const reason = codeOf(body)
  if (!reason) return {}
  return isPublicOnly(body) ? { reason, public_only: true } : { reason }
}

/**
 * The words a screen shows for a refusal that names its reason, translated by `t`, or null when it names none (the
 * screen then says what it said before). A publish-only refusal says the picture may be kept private, then why; any
 * other says why, then what to change (`fix`).
 */
export function refusalLine(
  t: T,
  body: unknown,
  fix: RefusalFix = 'prompt',
): string | null {
  const reason = refusalReason(body)
  if (!reason) return null
  const why = t(REASON_KEYS[reason])
  return isPublicOnly(body)
    ? `${t(PUBLIC_ONLY_KEY)} ${why}`
    : `${why} ${t(FIX_KEYS[fix])}`
}
